/**
 * /w/:slug/app/programs/:program/poam — V102-8-6: the CMMC POA&M view +
 * affirmation flow. The subcontractor-side surface for 32 CFR 170.21/170.22:
 * one 180-day clock per period, one closeout assessment, one closeout
 * affirmation signed by the named official.
 *
 * Reads (any member): the period's score, POA&M items, affirmation gate,
 * signed affirmations, team members. Writes (owner/admin or the program
 * lead; server-enforced):
 *   closePoamItem(poamId, official)   named-signer `cmmc_affirmation` on the
 *                                     last clocked close (ADR-0058 §4).
 *   requestAffirmation(kind, …)       initial / annual / poam_closeout /
 *                                     score_post, under the gate (D-UX-1).
 *   finalizeAffirmation(id)           after the official has signed.
 *   recordSprsEntry(id, {entered_on}) "Mark as entered in SPRS" (D-UX-4).
 *
 * Urgency sort (Exceptions.tsx pattern): past-due first, then open items with
 * fewest days left, then planned items last; closed items last of all.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea,
} from '@aigovops/design-system';
import type { AffirmationGate, AffirmationKind, AffirmationRecord, PoamItem } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { usePrograms } from '../lib/workspace-navigation';
import {
  closePoamItem, finalizeAffirmation, getAffirmationGate, getProgramScore, listAffirmations,
  listProgramPoam, listWorkspaceMembers, recordSprsEntry, requestAffirmation,
} from '../lib/api';
import { invalidationMap, queryKeys } from '../lib/query-keys';
import { ActionDialog, Chip, errorText, useAnnounce, useFocusAfter } from '../components/today/shared';
import { nameOf } from '../lib/today-model';
import { fmtShort, cmmcPeriod, riskTone, type Tone } from '../lib/cmmc-model';
import { useNames } from './Today';
import './today.css';
import './cert-ops.css';
import './cmmc.css';
import './programs.css';

const DAY = 86_400_000;

type RowStatus = 'past_due' | 'clocked' | 'planned' | 'closed' | 'expired';

interface PoamRow {
  item: PoamItem;
  ref: string;
  rowStatus: RowStatus;
  daysLeft: number | null;
  tone: Tone;
  chipText: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const refOf = (requirementId: string) => requirementId.replace(/^cmmc-l2:/, '').replace(/\[[^\]]*\]$/, '');

function urgency(r: PoamRow): number {
  if (r.rowStatus === 'past_due' || r.rowStatus === 'expired') return -1_000_000 + (r.daysLeft ?? 0);
  if (r.rowStatus === 'clocked') return r.daysLeft ?? 1_000;
  if (r.rowStatus === 'planned') return 1_000_000;
  return 2_000_000; // closed last
}

function toRow(item: PoamItem, now: number): PoamRow {
  const ref = refOf(item.requirement_id);
  if (item.status === 'closed') {
    return { item, ref, rowStatus: 'closed', daysLeft: null, tone: 'neutral', chipText: `Closed ${fmtShort(item.closed_at ?? item.updated_at)}` };
  }
  if (item.status === 'expired') {
    return { item, ref, rowStatus: 'expired', daysLeft: null, tone: 'red', chipText: `Expired ${fmtShort(item.expired_at ?? item.updated_at)}` };
  }
  if (item.closeout_due_at) {
    const daysLeft = Math.ceil((Date.parse(item.closeout_due_at) - now) / DAY);
    const past = daysLeft < 0;
    const tone = riskTone(daysLeft);
    return {
      item, ref, rowStatus: past ? 'past_due' : 'clocked', daysLeft, tone,
      chipText: past ? `Past due ${plural(-daysLeft, 'day', 'days')} · ${fmtShort(item.closeout_due_at)}` : `${plural(daysLeft, 'day', 'days')} left · ${fmtShort(item.closeout_due_at)}`,
    };
  }
  return { item, ref, rowStatus: 'planned', daysLeft: null, tone: 'neutral', chipText: 'Planned · clock starts at conditional status' };
}

const KIND_LABEL: Record<AffirmationKind, string> = {
  initial: 'Initial affirmation (certification)',
  annual: 'Annual affirmation',
  poam_closeout: 'POA&M closeout affirmation',
  score_post: 'Post score to SPRS (no CMMC status)',
};

const GATE_STEP_EXPLAIN: Record<string, string> = {
  not_assessed: 'Some requirements are not assessed yet.',
  awaiting_review: 'Some proposed results are awaiting a reviewer.',
  stale: 'Some Met results rest on evidence past its cadence.',
  no_ssp: 'The system security plan (3.12.4) is not in place.',
  not_pinned: 'The assessment snapshot is not pinned (move the period from Prepare to Audit).',
};

export function CmmcPoam() {
  const { program: rawProgram } = useParams<{ program: string }>();
  const program = rawProgram ?? 'cmmc';
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const auth = useAuth();
  const me = auth.user?.id ?? '';
  const names = useNames(slug);
  const [announceNode, announce] = useAnnounce();
  const focusLater = useFocusAfter();
  const qc = useQueryClient();

  const programs = usePrograms(slug);
  const card = useMemo(() => programs.data?.programs.find((p) => p.program === program) ?? null, [programs.data, program]);
  const period = useMemo(() => (card ? cmmcPeriod(card) : null), [card]);
  const periodId = period?.period_id ?? '';
  const canManage = role === 'owner' || role === 'admin';

  const score = useQuery({ queryKey: queryKeys.programScore(slug, program, periodId), queryFn: () => getProgramScore(slug, program, periodId), enabled: !!periodId, retry: false, staleTime: 30_000 });
  const poam = useQuery({ queryKey: queryKeys.programPoam(slug, program, periodId), queryFn: () => listProgramPoam(slug, program, periodId), enabled: !!periodId, retry: false, staleTime: 30_000 });
  const gate = useQuery({ queryKey: queryKeys.affirmationGate(slug, program, periodId), queryFn: () => getAffirmationGate(slug, program, periodId), enabled: !!periodId, retry: false, staleTime: 30_000 });
  const affs = useQuery({ queryKey: queryKeys.affirmations(slug, program), queryFn: () => listAffirmations(slug, program), enabled: !!periodId, retry: false, staleTime: 30_000 });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });

  const [closing, setClosing] = useState<PoamRow | null>(null);
  const [signing, setSigning] = useState<{ kind: AffirmationKind; poamId?: string } | null>(null);
  const [marking, setMarking] = useState<AffirmationRecord | null>(null);

  const refresh = () => Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));

  const now = Date.now();
  const rows = useMemo(() => {
    const list = (poam.data?.poam ?? []).map((item) => toRow(item, now)).sort((a, b) => urgency(a) - urgency(b) || a.ref.localeCompare(b.ref));
    return list;
  }, [poam.data, now]);

  const clocked = rows.filter((r) => r.rowStatus === 'clocked' || r.rowStatus === 'past_due' || r.rowStatus === 'expired');
  const openCount = rows.filter((r) => r.rowStatus === 'clocked' || r.rowStatus === 'past_due' || r.rowStatus === 'planned').length;
  const soonest = clocked.filter((r) => r.item.status === 'open' && typeof r.daysLeft === 'number').sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))[0];
  const anyLapsed = rows.some((r) => r.rowStatus === 'expired');
  const anyClocked = rows.some((r) => r.rowStatus === 'clocked' || r.rowStatus === 'past_due');
  const banner = soonest && soonest.daysLeft !== null && soonest.daysLeft < 14 ? soonest : null;

  const header = (
    <PageHeader variant="app" title="Plan of Action & Milestones" headingId="poam-h1"
      subtitle={card && period ? `${card.program === 'cmmc' ? 'CMMC Level 2' : card.program} · ${period.label}` : undefined} />
  );

  if (programs.isLoading || score.isLoading || poam.isLoading) {
    return <div className="gp-item">{header}<Spinner label="Loading the POA&M" /></div>;
  }
  if (!card || !period) {
    return (
      <div className="gp-item">
        {header}
        <EmptyState title="No CMMC program here yet"
          description="The POA&M view appears once the CMMC program has an assessment window."
          action={<Link className="gp-linkbtn" to={`${prefix}/programs`}>Open programs</Link>} />
      </div>
    );
  }
  if (score.error || poam.error) {
    return (
      <div className="gp-item">
        {header}
        <Alert variant="error"><p>The POA&M could not load.</p><Button onClick={() => { void score.refetch(); void poam.refetch(); }}>Retry</Button></Alert>
      </div>
    );
  }

  // Clock summary line.
  const clockLine = anyLapsed
    ? { text: 'Conditional status lapsed · the closeout clock passed before every item was closed.', tone: 'red' as const }
    : anyClocked && soonest?.item.closeout_due_at
      ? { text: `${plural(soonest.daysLeft ?? 0, 'day', 'days')} remaining to closeout · ${fmtShort(soonest.item.closeout_due_at)} · ${openCount} ${openCount === 1 ? 'item' : 'items'} open`, tone: (soonest.daysLeft ?? 0) < 14 ? 'red' as const : (soonest.daysLeft ?? 0) <= 30 ? 'amber' as const : 'neutral' as const }
      : rows.some((r) => r.rowStatus === 'planned')
        ? { text: 'Clock starts when the initial affirmation at conditional status is signed. Planned items wait until then.', tone: 'neutral' as const }
        : rows.length
          ? { text: 'Every POA&M item is closed.', tone: 'neutral' as const }
          : { text: 'No POA&M items yet.', tone: 'neutral' as const };

  return (
    <div className="gp-item" data-part="cmmc-poam">
      {announceNode}
      {header}
      <p className="gp-cmmc-toned" data-tone={clockLine.tone} data-part="clock-line">{clockLine.text}</p>

      {banner ? (
        <Alert variant="warning">
          <p>
            <strong>{`POA&M closeout in ${plural(banner.daysLeft ?? 0, 'day', 'days')}.`}</strong>{' '}
            Close every open item and finish with the closeout affirmation to keep conditional status.
          </p>
        </Alert>
      ) : null}

      <AffirmationPanel
        slug={slug} program={program} periodId={periodId} prefix={prefix}
        gate={gate.data?.gate ?? null} gateLoading={gate.isLoading}
        affirmations={affs.data?.affirmations ?? []} members={members.data?.members ?? []} names={names}
        canManage={canManage} viewerId={me}
        onSign={(kind) => setSigning({ kind })}
        onMarkEntered={(a) => setMarking(a)}
        onFinalize={async (a) => {
          try { await finalizeAffirmation(slug, a.affirmation_id); await refresh(); announce('Affirmation finalized.'); }
          catch (e) { announce(errorText(e, 'The affirmation could not be finalized.')); }
        }}
      />

      <section aria-labelledby="poam-list-h" className="gp-sec" data-part="poam-list">
        <h2 id="poam-list-h" className="gp-sec-h">{`POA&M items · ${rows.length}`}</h2>
        {rows.length === 0 ? (
          <EmptyState title="Nothing on the POA&M yet"
            description="A reviewed Not met result on an eligible requirement goes on the POA&M from the Prepare tab."
            action={<Link className="gp-linkbtn" to={`${prefix}/programs/${program}/prepare`}>Open Prepare</Link>} />
        ) : (
          <ul className="gp-owed-list" aria-labelledby="poam-list-h">
            {rows.map((r) => (
              <PoamRowItem key={r.item.poam_id} r={r} prefix={prefix} program={program} names={names}
                canManage={canManage}
                onClose={() => setClosing(r)}
              />
            ))}
          </ul>
        )}
      </section>

      <CloseDialog slug={slug} row={closing} members={members.data?.members ?? []} viewerId={me}
        onClose={() => setClosing(null)}
        onDone={async (msg, poamId) => {
          setClosing(null); await refresh();
          const i = rows.findIndex((r) => r.item.poam_id === poamId);
          const next = rows[i + 1] ?? rows[i - 1];
          focusLater(next ? `poam-row-${next.item.poam_id}` : 'poam-list-h');
          announce(msg);
        }} />

      <SignDialog slug={slug} program={program} periodId={periodId} picker={signing} members={members.data?.members ?? []} viewerId={me}
        onClose={() => setSigning(null)}
        onDone={async (msg) => { setSigning(null); await refresh(); announce(msg); }} />

      <MarkEnteredDialog slug={slug} affirmation={marking} onClose={() => setMarking(null)}
        onDone={async (msg) => { setMarking(null); await refresh(); announce(msg); }} />
    </div>
  );
}

/** The gate + signed-record list, sitting above the POA&M table. */
function AffirmationPanel({
  slug: _slug, program, periodId: _periodId, prefix, gate, gateLoading, affirmations, members: _members, names, canManage, viewerId,
  onSign, onMarkEntered, onFinalize,
}: {
  slug: string; program: string; periodId: string; prefix: string;
  gate: AffirmationGate | null; gateLoading: boolean;
  affirmations: ReadonlyArray<AffirmationRecord & { sprs_entry: import('@aigovops/core').SprsEntry | null }>;
  members: ReadonlyArray<{ user_id: string; display_name: string; email: string }>;
  names: ReadonlyMap<string, string>; canManage: boolean; viewerId: string;
  onSign: (kind: AffirmationKind) => void;
  onMarkEntered: (a: AffirmationRecord) => void;
  onFinalize: (a: AffirmationRecord) => Promise<void> | void;
}) {
  if (gateLoading) return <section className="gp-cmmc-gate" aria-label="Affirmation"><Spinner label="Loading the affirmation gate" /></section>;
  if (!gate) return null;
  const allowed = gate.allowed_kinds;
  const canSign = canManage;
  const latest = [...affirmations].sort((a, b) => (b.affirmed_at ?? b.created_at).localeCompare(a.affirmed_at ?? a.created_at));

  return (
    <section className="gp-cmmc-gate" aria-labelledby="poam-aff-h" data-part="aff-panel">
      <h2 id="poam-aff-h">Affirmations</h2>

      {!gate.finished ? (
        <>
          <p data-part="aff-line">Nothing can be signed yet — finish the self-assessment first.</p>
          <ul className="gp-cmmc-steps" aria-label="What is still open">
            {gate.open_steps.map((s) => (
              <li key={s} data-step={s}>
                <span className="gp-cmmc-step-head"><span>{GATE_STEP_EXPLAIN[s] ?? s}</span>
                  <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${program}/prepare`}>Open Prepare</Link>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : allowed.length === 0 ? (
        <p data-part="aff-line">Nothing to sign for this period.</p>
      ) : (
        <>
          {gate.at_status
            ? <p data-part="aff-line">Ready to affirm. The named official signs; the preparer cannot be the official.</p>
            : <p data-part="aff-line">Below CMMC status. The only signable record is the score posting for SPRS — <strong>no CMMC status</strong>, not an affirmation of compliance.</p>}
          <div className="gp-actions" data-part="aff-actions">
            {allowed.map((k) => (
              <Button key={k} variant={k === 'score_post' ? 'secondary' : 'primary'} compact
                disabled={!canSign} aria-disabled={!canSign || undefined}
                onClick={() => canSign && onSign(k)}>{KIND_LABEL[k]}</Button>
            ))}
          </div>
          {!canSign ? <p className="gp-cmmc-note" data-part="aff-note">Only an owner, admin, or program lead can request a signature.</p> : null}
        </>
      )}

      {latest.length ? (
        <ul className="gp-cmmc-rows" aria-label="Signed records" data-part="aff-records">
          {latest.slice(0, 6).map((a) => (
            <li key={a.affirmation_id} data-affirmation={a.affirmation_id}>
              <span className="gp-cmmc-name"><strong>{KIND_LABEL[a.kind]}</strong>{typeof a.score === 'number' ? <span className="gp-dim"> {` · score ${a.score}`}</span> : null}</span>
              <span className="gp-cmmc-meta">
                {a.state === 'affirmed' && a.affirmed_at
                  ? <>{`Signed by ${nameOf(names, a.official_user_id, 'the official')} on ${fmtShort(a.affirmed_at)}`}</>
                  : <>{`Awaiting ${nameOf(names, a.official_user_id, 'the official')}`}</>}
                {a.sprs_entry ? <Chip text={`Entered in SPRS ${fmtShort(a.sprs_entry.entered_on)}`} tone="ok" /> : null}
              </span>
              {a.state === 'requested' && a.official_user_id === viewerId
                ? <Button compact onClick={() => void onFinalize(a)} aria-label={`Record signature · ${KIND_LABEL[a.kind]}`}>Record signature</Button>
                : null}
              {a.state === 'affirmed' && !a.sprs_entry && canManage
                ? <Button compact variant="ghost" onClick={() => onMarkEntered(a)} aria-label={`Mark as entered in SPRS · ${KIND_LABEL[a.kind]}`}>Mark as entered in SPRS</Button>
                : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PoamRowItem({ r, prefix, program, names, canManage, onClose }: {
  r: PoamRow; prefix: string; program: string;
  names: ReadonlyMap<string, string>;
  canManage: boolean; onClose: () => void;
}) {
  const t = r.item;
  const owner = nameOf(names, t.owner_user_id, 'unassigned');
  const label = r.rowStatus === 'closed' ? 'Closed' : r.rowStatus === 'expired' ? 'Expired' : 'Open';

  let action: React.ReactNode = null;
  if (r.rowStatus === 'clocked' || r.rowStatus === 'past_due' || r.rowStatus === 'planned') {
    action = canManage
      ? <Button compact onClick={onClose} data-primary-action aria-label={`Close POA&M · ${r.ref}`}>Close item</Button>
      : <span className="gp-ow-meta"><span>Only an owner, admin, or program lead can close an item.</span></span>;
  } else if (r.rowStatus === 'expired') {
    action = (
      <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${program}/prepare`} data-primary-action>
        {`Open a new POA&M on the next period`}
      </Link>
    );
  } else if (r.rowStatus === 'closed' && t.closeout_determination_id) {
    action = <Link className="gp-cmmc-quiet" to={`${prefix}/programs/${program}/prepare#cmmc-r-${r.ref.replace(/[^A-Za-z0-9]+/g, '-')}`} data-primary-action>See closeout determination</Link>;
  }

  return (
    <li className="gp-ow" id={`poam-row-${t.poam_id}`} data-poam={t.poam_id} data-row-status={r.rowStatus} tabIndex={-1}>
      <span className="gp-ow-main">
        <span className="gp-ow-line">
          <Link className="gp-ow-t" to={`${prefix}/programs/${program}/prepare#cmmc-r-${r.ref.replace(/[^A-Za-z0-9]+/g, '-')}`}>{r.ref}</Link>
          <span className="gp-dim">{`${t.points_snapshot} ${t.points_snapshot === 1 ? 'pt' : 'pts'} · ${label}`}</span>
        </span>
        <span className="gp-ow-meta">
          <Chip text={r.chipText} tone={r.tone === 'red' ? 'red' : r.tone === 'amber' ? 'amber' : 'neutral'} />
          <span>{`Opened ${fmtShort(t.opened_at)}`}</span>
          <span>{`Owner ${owner}`}</span>
        </span>
        {t.plan ? <span className="gp-ow-meta">{t.plan}</span> : null}
      </span>
      <span className="gp-ow-act">{action}</span>
    </li>
  );
}

function CloseDialog({ slug, row, members, viewerId, onClose, onDone }: {
  slug: string; row: PoamRow | null; members: ReadonlyArray<{ user_id: string; display_name: string; email: string }>;
  viewerId: string; onClose: () => void; onDone: (msg: string, poamId: string) => Promise<void>;
}) {
  const [official, setOfficial] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Preparer != official (ADR-0046).
  const options = members.filter((m) => m.user_id !== viewerId);

  return (
    <ActionDialog open={!!row} title={row ? `Close POA&M · ${row.ref}` : 'Close POA&M item'}
      submitLabel="Close item" busy={busy} error={err}
      onClose={() => { setErr(null); setOfficial(''); onClose(); }}
      onSubmit={async () => {
        if (!row) return false;
        if (!official) { setErr('Choose the affirming official.'); return false; }
        setBusy(true); setErr(null);
        try {
          const out = await closePoamItem(slug, row.item.poam_id, { official_user_id: official });
          const msg = out.affirmation ? `Closed · closeout affirmation sent to the named official.` : `Closed ${row.ref}.`;
          setOfficial('');
          await onDone(msg, row.item.poam_id);
          return true;
        } catch (x) { setErr(errorText(x, 'The item could not be closed.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">Closing the last clocked open item requests the single closeout affirmation (32 CFR 170.21(a)(4)). The person closing cannot also be the affirming official.</p>
      <Field id="close-official" label="Affirming official" required
        hint="A workspace member; not you.">
        <Select value={official} onChange={(e) => { setOfficial(e.currentTarget.value); setErr(null); }}>
          <option value="">Choose…</option>
          {options.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name || m.email}</option>)}
        </Select>
      </Field>
    </ActionDialog>
  );
}

function SignDialog({ slug, program, periodId, picker, members, viewerId, onClose, onDone }: {
  slug: string; program: string; periodId: string;
  picker: { kind: AffirmationKind; poamId?: string } | null;
  members: ReadonlyArray<{ user_id: string; display_name: string; email: string }>;
  viewerId: string; onClose: () => void; onDone: (msg: string) => Promise<void>;
}) {
  const [official, setOfficial] = useState('');
  const [assessmentDate, setAssessmentDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedCompletion, setExpectedCompletion] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const options = members.filter((m) => m.user_id !== viewerId);
  const isScorePost = picker?.kind === 'score_post';

  return (
    <ActionDialog open={!!picker} title={picker ? `Request · ${KIND_LABEL[picker.kind]}` : 'Request affirmation'}
      submitLabel={isScorePost ? 'Request SPRS posting' : 'Request affirmation'} busy={busy} error={err}
      onClose={() => { setErr(null); setOfficial(''); setExpectedCompletion(''); onClose(); }}
      onSubmit={async () => {
        if (!picker) return false;
        if (!official) { setErr('Choose the affirming official.'); return false; }
        if (isScorePost && !expectedCompletion) { setErr('SPRS needs the date all requirements will be met.'); return false; }
        setBusy(true); setErr(null);
        try {
          await requestAffirmation(slug, {
            program, period_id: periodId, kind: picker.kind, poam_id: picker.poamId ?? null,
            official_user_id: official, assessment_date: assessmentDate || null,
            expected_completion_date: isScorePost ? expectedCompletion : null,
          });
          setOfficial(''); setExpectedCompletion('');
          await onDone(isScorePost ? 'Score posting sent to the named official.' : 'Affirmation sent to the named official.');
          return true;
        } catch (x) { setErr(errorText(x, 'The affirmation could not be requested.')); return false; }
        finally { setBusy(false); }
      }}>
      {isScorePost ? (
        <p className="gp-dim">A score posting records the score for SPRS with <strong>no CMMC status</strong>. It is not an affirmation of compliance.</p>
      ) : (
        <p className="gp-dim">The named official signs this statement; GAC Pro never submits to SPRS. The preparer and the official cannot be the same person.</p>
      )}
      <Field id="sign-official" label="Affirming official (named signer)" required hint="A workspace member; not you.">
        <Select value={official} onChange={(e) => { setOfficial(e.currentTarget.value); setErr(null); }}>
          <option value="">Choose…</option>
          {options.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name || m.email}</option>)}
        </Select>
      </Field>
      <Field id="sign-assessment" label="Assessment date (SPRS)" hint="Past or present day (YYYY-MM-DD).">
        <Input type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.currentTarget.value)} />
      </Field>
      {isScorePost ? (
        <Field id="sign-expected" label="Expected completion date" required hint="When all requirements will be met (YYYY-MM-DD).">
          <Input type="date" value={expectedCompletion} onChange={(e) => setExpectedCompletion(e.currentTarget.value)} />
        </Field>
      ) : null}
    </ActionDialog>
  );
}

function MarkEnteredDialog({ slug, affirmation, onClose, onDone }: {
  slug: string; affirmation: AffirmationRecord | null; onClose: () => void; onDone: (msg: string) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [enteredOn, setEnteredOn] = useState(today);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <ActionDialog open={!!affirmation} title={affirmation ? `Mark entered in SPRS · ${KIND_LABEL[affirmation.kind]}` : 'Mark entered in SPRS'}
      submitLabel="Mark as entered" busy={busy} error={err}
      onClose={() => { setErr(null); setNote(''); onClose(); }}
      onSubmit={async () => {
        if (!affirmation) return false;
        if (!enteredOn) { setErr('Choose the day it was entered.'); return false; }
        setBusy(true); setErr(null);
        try {
          await recordSprsEntry(slug, affirmation.affirmation_id, { entered_on: enteredOn, note: note.trim() || null });
          setNote('');
          await onDone('Marked as entered in SPRS.');
          return true;
        } catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">GAC Pro never submits to SPRS. Record when the customer entered this signed record in SPRS.</p>
      <Field id="sprs-day" label="Entered on" required>
        <Input type="date" max={today} value={enteredOn} onChange={(e) => setEnteredOn(e.currentTarget.value)} />
      </Field>
      <Field id="sprs-note" label="Note (optional)" hint="Up to 2000 characters.">
        <Textarea rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

/** Discard unused imports (SPA tooling strict). */
void useEffect;
